import { useState } from 'react';
import { 
  Calculator, 
  Battery, 
  Volume2, 
  Ruler, 
  FileDown,
  AlertCircle,
  CheckCircle2,
  Info,
  Zap,
  FileText
} from 'lucide-react';
import { ResourcesModal } from '../components/ResourcesModal';

interface ToolCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}

function ToolCard({ icon, title, description, children }: ToolCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center mb-4">
        <div className="p-3 bg-blue-100 rounded-lg">
          {icon}
        </div>
        <div className="ml-4">
          <h3 className="text-xl font-bold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-600">{description}</p>
        </div>
      </div>
      <div className="mt-6">
        {children}
      </div>
    </div>
  );
}

function BatteryCalculator() {
  const [systemCurrent, setSystemCurrent] = useState<string>('');
  const [alarmCurrent, setAlarmCurrent] = useState<string>('');
  const [alarmDuration, setAlarmDuration] = useState<string>('0.25');
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const standby = parseFloat(systemCurrent) || 0;
    const alarm = parseFloat(alarmCurrent) || 0;
    const duration = parseFloat(alarmDuration) || 0.25;
    
    // NFPA 72: 24 hours standby + alarm time (typically 5 or 15 minutes)
    const standbyAh = standby * 24;
    const alarmAh = alarm * duration;
    const totalAh = (standbyAh + alarmAh) * 1.2; // 20% safety factor
    
    setResult(totalAh);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Standby Current (Amps)
        </label>
        <input
          type="number"
          step="0.01"
          value={systemCurrent}
          onChange={(e) => setSystemCurrent(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., 0.5"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Alarm Current (Amps)
        </label>
        <input
          type="number"
          step="0.01"
          value={alarmCurrent}
          onChange={(e) => setAlarmCurrent(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., 2.5"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Alarm Duration (Hours)
        </label>
        <select
          value={alarmDuration}
          onChange={(e) => setAlarmDuration(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="0.083">5 minutes (0.083 hrs)</option>
          <option value="0.25">15 minutes (0.25 hrs)</option>
          <option value="0.5">30 minutes (0.5 hrs)</option>
        </select>
      </div>
      <button
        onClick={calculate}
        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
      >
        Calculate Battery Capacity
      </button>
      {result !== null && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
            <span className="font-semibold text-green-900">Required Battery Capacity:</span>
          </div>
          <p className="text-2xl font-bold text-green-700 mt-2">{result.toFixed(2)} Ah</p>
          <p className="text-sm text-green-700 mt-2">
            Includes 20% safety factor per NFPA 72
          </p>
        </div>
      )}
    </div>
  );
}

function NotificationCalculator() {
  const [roomLength, setRoomLength] = useState<string>('');
  const [roomWidth, setRoomWidth] = useState<string>('');
  const [ceilingHeight, setCeilingHeight] = useState<string>('');
  const [ambientDb, setAmbientDb] = useState<string>('55');
  const [result, setResult] = useState<{ strobes: number; horns: number; db: number } | null>(null);

  const calculate = () => {
    const length = parseFloat(roomLength) || 0;
    const width = parseFloat(roomWidth) || 0;
    const ambient = parseFloat(ambientDb) || 55;
    
    const area = length * width;
    const requiredDb = ambient + 15; // NFPA 72: 15 dB above ambient
    
    // Strobe calculation (simplified - based on room size)
    let strobes = 1;
    if (area > 1000) strobes = Math.ceil(area / 1000);
    
    // Horn calculation (simplified - based on coverage)
    const horns = Math.ceil(area / 2500); // Typical 50ft coverage radius
    
    setResult({ strobes, horns, db: requiredDb });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Room Length (ft)
          </label>
          <input
            type="number"
            value={roomLength}
            onChange={(e) => setRoomLength(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., 50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Room Width (ft)
          </label>
          <input
            type="number"
            value={roomWidth}
            onChange={(e) => setRoomWidth(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., 40"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Ceiling Height (ft)
        </label>
        <input
          type="number"
          value={ceilingHeight}
          onChange={(e) => setCeilingHeight(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., 10"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Ambient Sound Level (dB)
        </label>
        <select
          value={ambientDb}
          onChange={(e) => setAmbientDb(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="45">Quiet Office (45 dB)</option>
          <option value="55">Normal Office (55 dB)</option>
          <option value="65">Busy Office (65 dB)</option>
          <option value="75">Mechanical Room (75 dB)</option>
          <option value="85">Industrial (85 dB)</option>
        </select>
      </div>
      <button
        onClick={calculate}
        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
      >
        Calculate Notification Appliances
      </button>
      {result !== null && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
          <div className="flex items-center">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2" />
            <span className="font-semibold text-blue-900">Estimated Requirements:</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-3 rounded-lg">
              <p className="text-sm text-slate-600">Strobes Needed</p>
              <p className="text-2xl font-bold text-blue-700">{result.strobes}</p>
            </div>
            <div className="bg-white p-3 rounded-lg">
              <p className="text-sm text-slate-600">Horn/Speakers</p>
              <p className="text-2xl font-bold text-blue-700">{result.horns}</p>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg">
            <p className="text-sm text-slate-600">Required Sound Level</p>
            <p className="text-xl font-bold text-blue-700">{result.db} dB</p>
          </div>
          <p className="text-xs text-blue-700">
            <Info className="w-4 h-4 inline mr-1" />
            This is a simplified calculation. Actual requirements depend on occupancy type, mounting height, and AHJ requirements.
          </p>
        </div>
      )}
    </div>
  );
}

function DetectorSpacingCalculator() {
  const [roomLength, setRoomLength] = useState<string>('');
  const [roomWidth, setRoomWidth] = useState<string>('');
  const [ceilingHeight, setCeilingHeight] = useState<string>('');
  const [detectorType, setDetectorType] = useState<string>('smoke');
  const [result, setResult] = useState<{ detectors: number; spacing: number } | null>(null);

  const calculate = () => {
    const length = parseFloat(roomLength) || 0;
    const width = parseFloat(roomWidth) || 0;
    const height = parseFloat(ceilingHeight) || 10;
    
    // NFPA 72 spacing requirements
    let maxSpacing = 30; // Default for smoke detectors
    if (detectorType === 'heat') {
      maxSpacing = height <= 10 ? 30 : 25;
    }
    
    const area = length * width;
    const coverageArea = Math.PI * Math.pow(maxSpacing / 2, 2);
    const detectors = Math.ceil(area / coverageArea);
    
    setResult({ detectors, spacing: maxSpacing });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Room Length (ft)
          </label>
          <input
            type="number"
            value={roomLength}
            onChange={(e) => setRoomLength(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., 60"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Room Width (ft)
          </label>
          <input
            type="number"
            value={roomWidth}
            onChange={(e) => setRoomWidth(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., 40"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Ceiling Height (ft)
        </label>
        <input
          type="number"
          value={ceilingHeight}
          onChange={(e) => setCeilingHeight(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., 10"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Detector Type
        </label>
        <select
          value={detectorType}
          onChange={(e) => setDetectorType(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="smoke">Smoke Detector</option>
          <option value="heat">Heat Detector</option>
        </select>
      </div>
      <button
        onClick={calculate}
        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
      >
        Calculate Detector Spacing
      </button>
      {result !== null && (
        <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center mb-3">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mr-2" />
            <span className="font-semibold text-purple-900">Spacing Results:</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-3 rounded-lg">
              <p className="text-sm text-slate-600">Detectors Needed</p>
              <p className="text-2xl font-bold text-purple-700">{result.detectors}</p>
            </div>
            <div className="bg-white p-3 rounded-lg">
              <p className="text-sm text-slate-600">Max Spacing</p>
              <p className="text-2xl font-bold text-purple-700">{result.spacing} ft</p>
            </div>
          </div>
          <p className="text-xs text-purple-700 mt-3">
            <Info className="w-4 h-4 inline mr-1" />
            Per NFPA 72. Actual spacing may be reduced based on ceiling configuration, obstructions, and specific detector listings.
          </p>
        </div>
      )}
    </div>
  );
}

function VoltageDropCalculator() {
  const [wireSize, setWireSize] = useState<string>('14');
  const [distance, setDistance] = useState<string>('');
  const [current, setCurrent] = useState<string>('');
  const [result, setResult] = useState<{ voltageDrop: number; percentage: number; acceptable: boolean } | null>(null);

  const calculate = () => {
    const dist = parseFloat(distance) || 0;
    const amps = parseFloat(current) || 0;
    
    // Resistance per 1000ft for copper wire at 75°C
    const resistance: { [key: string]: number } = {
      '18': 7.77,
      '16': 4.89,
      '14': 3.07,
      '12': 1.93,
      '10': 1.21,
    };
    
    const r = resistance[wireSize] || 3.07;
    const voltageDrop = (2 * dist * amps * r) / 1000; // 2 for round trip
    const percentage = (voltageDrop / 24) * 100; // Assuming 24V system
    const acceptable = percentage <= 10; // NFPA 72: max 10% voltage drop
    
    setResult({ voltageDrop, percentage, acceptable });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Wire Size (AWG)
        </label>
        <select
          value={wireSize}
          onChange={(e) => setWireSize(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="18">18 AWG</option>
          <option value="16">16 AWG</option>
          <option value="14">14 AWG</option>
          <option value="12">12 AWG</option>
          <option value="10">10 AWG</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Distance (feet)
        </label>
        <input
          type="number"
          value={distance}
          onChange={(e) => setDistance(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., 200"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Current (Amps)
        </label>
        <input
          type="number"
          step="0.01"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., 0.5"
        />
      </div>
      <button
        onClick={calculate}
        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
      >
        Calculate Voltage Drop
      </button>
      {result !== null && (
        <div className={`mt-4 p-4 border rounded-lg ${result.acceptable ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center mb-3">
            {result.acceptable ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            )}
            <span className={`font-semibold ${result.acceptable ? 'text-green-900' : 'text-red-900'}`}>
              {result.acceptable ? 'Acceptable' : 'Exceeds Limit'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-3 rounded-lg">
              <p className="text-sm text-slate-600">Voltage Drop</p>
              <p className={`text-2xl font-bold ${result.acceptable ? 'text-green-700' : 'text-red-700'}`}>
                {result.voltageDrop.toFixed(2)} V
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg">
              <p className="text-sm text-slate-600">Percentage</p>
              <p className={`text-2xl font-bold ${result.acceptable ? 'text-green-700' : 'text-red-700'}`}>
                {result.percentage.toFixed(1)}%
              </p>
            </div>
          </div>
          <p className={`text-xs mt-3 ${result.acceptable ? 'text-green-700' : 'text-red-700'}`}>
            <Info className="w-4 h-4 inline mr-1" />
            NFPA 72 requires voltage drop not to exceed 10% for notification appliance circuits.
          </p>
        </div>
      )}
    </div>
  );
}

export function ToolsPage() {
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [resourceCategory, setResourceCategory] = useState<'template' | 'code_reference' | 'guide' | 'all'>('all');
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Calculator className="w-16 h-16 mx-auto mb-4" />
            <h1 data-testid="page-h1-tools" className="text-4xl md:text-5xl font-bold mb-4">
              Tools
            </h1>
            <p className="text-xl text-purple-100 mb-8">
              Free online calculators for fire alarm system design and NFPA 72 compliance
            </p>
            <div className="flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 inline-flex">
              <Info className="w-5 h-5 mr-2" />
              <span>All calculations based on NFPA 72 requirements</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tools Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Battery Calculator */}
          <ToolCard
            icon={<Battery className="w-6 h-6 text-blue-500" />}
            title="Battery Capacity Calculator"
            description="Calculate required battery capacity per NFPA 72"
          >
            <BatteryCalculator />
          </ToolCard>

          {/* Notification Appliance Calculator */}
          <ToolCard
            icon={<Volume2 className="w-6 h-6 text-blue-500" />}
            title="Notification Appliance Calculator"
            description="Estimate horn/strobe requirements for your space"
          >
            <NotificationCalculator />
          </ToolCard>

          {/* Detector Spacing Calculator */}
          <ToolCard
            icon={<Ruler className="w-6 h-6 text-blue-500" />}
            title="Detector Spacing Calculator"
            description="Calculate smoke and heat detector spacing"
          >
            <DetectorSpacingCalculator />
          </ToolCard>

          {/* Voltage Drop Calculator */}
          <ToolCard
            icon={<Zap className="w-6 h-6 text-blue-500" />}
            title="Voltage Drop Calculator"
            description="Verify NAC circuit voltage drop compliance"
          >
            <VoltageDropCalculator />
          </ToolCard>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-2">Important Disclaimer</h3>
                <p className="text-sm text-yellow-800">
                  These calculators are provided as preliminary design tools only. Results are simplified estimates 
                  and should not be used as the sole basis for system design. Actual fire alarm system design must 
                  consider numerous factors including specific equipment listings, manufacturer specifications, 
                  ceiling configurations, ambient conditions, and local authority requirements. All designs should 
                  be prepared by qualified professionals and reviewed by the authority having jurisdiction (AHJ).
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Additional Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <button
              onClick={() => {
                setResourceCategory('template');
                setShowResourcesModal(true);
              }}
              className="text-center p-6 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-xl transition-all duration-200 cursor-pointer group"
            >
              <div className="p-4 bg-blue-100 group-hover:bg-blue-200 rounded-lg inline-block mb-3 transition-colors">
                <FileDown className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Design Templates</h3>
              <p className="text-sm text-slate-600 mb-4">
                Download our free fire alarm design templates and checklists
              </p>
              <span className="inline-flex items-center text-blue-600 font-semibold text-sm">
                Browse Templates →
              </span>
            </button>
            <button
              onClick={() => {
                setResourceCategory('code_reference');
                setShowResourcesModal(true);
              }}
              className="text-center p-6 rounded-xl border-2 border-slate-200 hover:border-green-400 hover:shadow-xl transition-all duration-200 cursor-pointer group"
            >
              <div className="p-4 bg-green-100 group-hover:bg-green-200 rounded-lg inline-block mb-3 transition-colors">
                <FileText className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Code References</h3>
              <p className="text-sm text-slate-600 mb-4">
                Quick reference guides for NFPA 72, NEC 760, and IBC/IFC
              </p>
              <span className="inline-flex items-center text-green-600 font-semibold text-sm">
                Browse References →
              </span>
            </button>
            <button
              onClick={() => {
                setResourceCategory('guide');
                setShowResourcesModal(true);
              }}
              className="text-center p-6 rounded-xl border-2 border-slate-200 hover:border-purple-400 hover:shadow-xl transition-all duration-200 cursor-pointer group"
            >
              <div className="p-4 bg-purple-100 group-hover:bg-purple-200 rounded-lg inline-block mb-3 transition-colors">
                <Calculator className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Calculation Guides</h3>
              <p className="text-sm text-slate-600 mb-4">
                Step-by-step guides for fire alarm calculations
              </p>
              <span className="inline-flex items-center text-purple-600 font-semibold text-sm">
                Browse Guides →
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-slate-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need Professional Design Services?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Our licensed engineers can handle your complete fire alarm system design
          </p>
          <a 
            href="/services"
            className="inline-flex items-center px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors duration-200"
          >
            View Our Services
          </a>
        </div>
      </div>
      
      {/* Resources Modal */}
      {showResourcesModal && (
        <ResourcesModal
          onClose={() => setShowResourcesModal(false)}
          initialCategory={resourceCategory}
        />
      )}
    </div>
  );
}
